/**
 * useLiveInterviewRecorder — composites the room's video tiles onto a hidden
 * canvas (captureStream at 10fps) and mixes their audio via AudioContext/
 * MediaStreamAudioDestinationNode, records the combined stream with
 * MediaRecorder, and hands each chunk to the caller-supplied `uploadChunk`
 * (start()'s first argument) rather than posting to a hardcoded endpoint
 * itself — this hook runs on the RECRUITER's side in this app (unlike VMP,
 * where the candidate's browser records), so the caller decides how a chunk
 * actually gets uploaded (liveInterviewApi.postRecordingChunk, authenticated
 * by session id) rather than this hook assuming a candidate access_token is
 * available. Chunk uploads are chained into a single sequential promise
 * (`uploadChainRef`) rather than fired in parallel, so chunk N+1 never
 * lands before chunk N even if one request is slow — the backend can assume
 * in-order arrival.
 *
 * Phase C (Part 0): the room's tile list is no longer fixed at start() time
 * — peers join/leave over a call's lifetime as WebRTC connections come up
 * and go down (see useLiveInterviewCall). `start()` takes a *ref* to the
 * current tile list rather than a plain array, and `updateTiles()` lets the
 * room page push a new list in as it changes; drawFrame() re-reads
 * `tilesRef.current` on every animation frame so the composited grid
 * reflows automatically as tiles are added/removed. Audio is handled
 * separately: each tile's MediaStream is connected into the mix (via
 * AudioContext.createMediaStreamSource) at most once — connectedStreamsRef
 * tracks which streams already have a source node so updateTiles() can be
 * called repeatedly (e.g. once per peer join) without double-connecting the
 * same stream's audio into the destination twice. A stream that leaves is
 * left connected (its tracks stop producing audio once the peer's
 * connection closes) rather than torn down — disconnecting a
 * MediaStreamAudioSourceNode mid-recording is unnecessary complexity for
 * what is already a best-effort composited recording, not the call itself.
 */

import { useRef, useCallback } from 'react'

export interface RecorderTile {
  video: HTMLVideoElement
  stream: MediaStream
  /** While this tile is a PRESENTER's screen share, the same participant's camera feed - drawn in
   *  the camera strip below the main stage (see drawFrame). Absent for anyone not presenting. */
  overlayVideo?: HTMLVideoElement
  /**
   * The MediaStream behind `overlayVideo` — needed separately from the video
   * element because getDisplayMedia's screen stream (this tile's main
   * `stream` while presenting) carries no audio track at all: the
   * presenter's own mic only exists on THIS stream, so connectNewAudioTiles
   * must mix it in too or their voice silently drops out of the recording
   * for as long as they're sharing.
   */
  overlayStream?: MediaStream
}

/**
 * Preference-ordered list of mimeTypes to try for the composited recording.
 * Not every browser supports every codec combo (e.g. Safari lacks vp8/vp9 in
 * MediaRecorder), so we probe with MediaRecorder.isTypeSupported() rather
 * than hardcoding one and letting the constructor throw synchronously.
 */
const MIME_TYPE_PREFERENCES = ['video/webm;codecs=vp8,opus', 'video/webm;codecs=vp9,opus', 'video/webm']

function pickSupportedMimeType(): string | undefined {
  if (typeof MediaRecorder === 'undefined' || typeof MediaRecorder.isTypeSupported !== 'function') {
    return undefined
  }
  return MIME_TYPE_PREFERENCES.find((mimeType) => {
    try {
      return MediaRecorder.isTypeSupported(mimeType)
    } catch {
      return false
    }
  })
}

// Screen-share as the big main stage on top, every camera (the presenter's overlayVideo, or a
// non-presenter's own video) in a small non-overlapping strip below it.
function drawScreenShareLayout(ctx: CanvasRenderingContext2D, canvas: HTMLCanvasElement, presenting: RecorderTile, tiles: RecorderTile[]) {
  const stageH = canvas.height * 0.72
  if (presenting.video.readyState >= 2) ctx.drawImage(presenting.video, 0, 0, canvas.width, stageH)
  const stripH = canvas.height - stageH
  const cameras = tiles.map((t) => t.overlayVideo ?? t.video)
  const tileW = canvas.width / (cameras.length || 1)
  cameras.forEach((video, i) => {
    if (video.readyState >= 2) ctx.drawImage(video, i * tileW, stageH, tileW, stripH)
  })
}

// AI-interviewer mode: the candidate's entire screen fills the frame
// (letterboxed to keep its aspect ratio), with their camera inset bottom-right.
function drawFullScreenLayout(ctx: CanvasRenderingContext2D, canvas: HTMLCanvasElement, screen: HTMLVideoElement, tiles: RecorderTile[]) {
  const sw = screen.videoWidth || canvas.width
  const sh = screen.videoHeight || canvas.height
  const scale = Math.min(canvas.width / sw, canvas.height / sh)
  const w = sw * scale
  const h = sh * scale
  ctx.drawImage(screen, (canvas.width - w) / 2, (canvas.height - h) / 2, w, h)
  const camera = tiles[0]?.overlayVideo ?? tiles[0]?.video
  if (camera && camera.readyState >= 2) {
    const pipW = canvas.width * 0.2
    const pipH = pipW * ((camera.videoHeight || 9) / (camera.videoWidth || 16))
    const x = canvas.width - pipW - 16
    const y = canvas.height - pipH - 16
    ctx.fillStyle = '#000'
    ctx.fillRect(x - 2, y - 2, pipW + 4, pipH + 4)
    ctx.drawImage(camera, x, y, pipW, pipH)
  }
}

// No one presenting: plain equal-size grid of camera tiles.
function drawGridLayout(ctx: CanvasRenderingContext2D, canvas: HTMLCanvasElement, tiles: RecorderTile[]) {
  const cols = Math.ceil(Math.sqrt(tiles.length || 1))
  const tileW = canvas.width / cols
  const tileH = canvas.height / Math.ceil((tiles.length || 1) / cols)
  tiles.forEach(({ video }, i) => {
    const x = (i % cols) * tileW
    const y = Math.floor(i / cols) * tileH
    if (video.readyState >= 2) ctx.drawImage(video, x, y, tileW, tileH)
  })
}

export function useLiveInterviewRecorder() {
  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const drawFrameRef = useRef<number | null>(null)
  const uploadChainRef = useRef<Promise<void>>(Promise.resolve())
  const chunkIndexRef = useRef(0)
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const audioContextRef = useRef<AudioContext | null>(null)
  const audioDestinationRef = useRef<MediaStreamAudioDestinationNode | null>(null)
  const tilesRef = useRef<RecorderTile[]>([])
  const connectedStreamsRef = useRef<Set<MediaStream>>(new Set())
  // AI-interviewer mode: the candidate's whole screen, recorded full-frame
  // with their camera inset (see setScreenSource). Null = normal layouts.
  const screenVideoRef = useRef<HTMLVideoElement | null>(null)

  // Connects any tile's audio track that isn't already feeding the mix —
  // safe to call repeatedly (e.g. once per peer join/leave) since already-
  // connected streams are tracked and skipped. A presenting tile's `stream`
  // is their SCREEN (no audio track — getDisplayMedia is captured with
  // audio:false), so `overlayStream` (their camera, carrying their mic) must
  // be connected too or their voice silently drops out of the recording for
  // as long as they're sharing.
  const connectNewAudioTiles = useCallback((tiles: RecorderTile[]) => {
    const audioContext = audioContextRef.current
    const destination = audioDestinationRef.current
    if (!audioContext || !destination) return
    const connectStream = (stream: MediaStream) => {
      if (connectedStreamsRef.current.has(stream)) return
      if (stream.getAudioTracks().length === 0) return
      audioContext.createMediaStreamSource(stream).connect(destination)
      connectedStreamsRef.current.add(stream)
    }
    tiles.forEach(({ stream, overlayStream }) => {
      connectStream(stream)
      if (overlayStream) connectStream(overlayStream)
    })
  }, [])

  // Called by the room page whenever its tile list changes (a peer's
  // RTCPeerConnection comes up/goes down) — updates what drawFrame()
  // composites next frame and connects any newly-seen stream's audio.
  const updateTiles = useCallback(
    (tiles: RecorderTile[]) => {
      tilesRef.current = tiles
      connectNewAudioTiles(tiles)
    },
    [connectNewAudioTiles]
  )

  // Mixes an extra audio-only source into the recording — the AI
  // interviewer's synthesized voice, which plays to the speakers and so is
  // on no participant tile. Safe to call repeatedly; no-op while stopped.
  const addAudioStream = useCallback((stream: MediaStream) => {
    const audioContext = audioContextRef.current
    const destination = audioDestinationRef.current
    if (!audioContext || !destination) return
    if (connectedStreamsRef.current.has(stream) || stream.getAudioTracks().length === 0) return
    try {
      audioContext.createMediaStreamSource(stream).connect(destination)
      connectedStreamsRef.current.add(stream)
    } catch { /* this source just isn't recorded */ }
  }, [])

  // AI-interviewer mode: record the candidate's shared screen as the main
  // picture. Pass null to fall back to the camera layouts (e.g. they stopped
  // sharing). Can be called before or after start().
  const setScreenSource = useCallback((video: HTMLVideoElement | null) => {
    screenVideoRef.current = video
  }, [])

  const start = useCallback(
    (
      uploadChunk: (index: number, chunk: Blob) => Promise<unknown>,
      initialTiles: RecorderTile[],
      chunkIntervalMs: number
    ) => {
      tilesRef.current = initialTiles
      const canvas = document.createElement('canvas')
      canvas.width = 1280
      canvas.height = 720
      canvasRef.current = canvas
      const ctx = canvas.getContext('2d')!

      // Teams-style: whoever's presenting takes the big main stage, every
      // participant's camera goes in a small strip below it - never a
      // literal screen capture, and never screen-share squeezed into one
      // small equal grid cell. No presenter: plain equal camera grid.
      const drawFrame = () => {
        ctx.fillStyle = '#000'
        ctx.fillRect(0, 0, canvas.width, canvas.height)
        const tiles = tilesRef.current
        const presenting = tiles.find((t) => t.overlayVideo)
        const screenVideo = screenVideoRef.current
        if (screenVideo && screenVideo.readyState >= 2) {
          drawFullScreenLayout(ctx, canvas, screenVideo, tiles)
        } else if (presenting) {
          drawScreenShareLayout(ctx, canvas, presenting, tiles)
        } else {
          drawGridLayout(ctx, canvas, tiles)
        }
        drawFrameRef.current = requestAnimationFrame(drawFrame)
      }
      drawFrame()

      const audioContext = new AudioContext()
      // Started without a fresh click in AI mode — make sure it actually runs.
      if (audioContext.state === 'suspended') audioContext.resume().catch(() => {})
      audioContextRef.current = audioContext
      const destination = audioContext.createMediaStreamDestination()
      audioDestinationRef.current = destination
      connectNewAudioTiles(tilesRef.current)

      const canvasStream = canvas.captureStream(10)
      const combined = new MediaStream([...canvasStream.getVideoTracks(), ...destination.stream.getAudioTracks()])

      const mimeType = pickSupportedMimeType()
      let mediaRecorder: MediaRecorder
      try {
        mediaRecorder = mimeType ? new MediaRecorder(combined, { mimeType }) : new MediaRecorder(combined)
      } catch (err) {
        // Recording is a best-effort enhancement — if no supported codec is
        // available (e.g. Safari), fail loudly in the console but don't let
        // the exception propagate out of this effect-invoked callback: an
        // uncaught error here isn't caught by React error boundaries and
        // would otherwise leave the caller in an unknown state. The room's
        // local video preview should still work without recording.
        console.error('Live interview recorder: failed to create MediaRecorder; recording disabled.', err)
        if (drawFrameRef.current) {
          cancelAnimationFrame(drawFrameRef.current)
          drawFrameRef.current = null
        }
        audioContext.close()
        audioContextRef.current = null
        audioDestinationRef.current = null
        canvasRef.current = null
        return
      }
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          const index = chunkIndexRef.current++
          uploadChainRef.current = uploadChainRef.current.then(() =>
            uploadChunk(index, event.data)
              .then(() => undefined)
              .catch((err: unknown) => console.warn('Live interview recorder: chunk upload failed.', err))
          )
        }
      }
      mediaRecorder.start(chunkIntervalMs)
      mediaRecorderRef.current = mediaRecorder
    },
    [connectNewAudioTiles]
  )

  // Returns a promise that resolves once the final chunk (the one MediaRecorder
  // fires from .stop() itself) has actually finished uploading — not just
  // been queued. Per the MediaRecorder spec, a pending 'dataavailable' fires
  // (synchronously chaining onto uploadChainRef) before 'stop' fires, so by
  // the time onstop runs, awaiting uploadChainRef.current covers that last
  // chunk too. Callers that need the recording to be fully persisted before
  // moving on (e.g. ending the interview) should await this instead of
  // firing-and-forgetting it.
  const stop = useCallback(() => {
    const mediaRecorder = mediaRecorderRef.current
    mediaRecorderRef.current = null
    if (drawFrameRef.current) {
      cancelAnimationFrame(drawFrameRef.current)
      drawFrameRef.current = null
    }
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      audioContextRef.current.close()
    }
    audioContextRef.current = null
    audioDestinationRef.current = null
    canvasRef.current = null
    tilesRef.current = []
    connectedStreamsRef.current = new Set()

    if (!mediaRecorder || mediaRecorder.state === 'inactive') return Promise.resolve()
    return new Promise<void>((resolve) => {
      mediaRecorder.onstop = () => {
        uploadChainRef.current.then(() => resolve(), () => resolve())
      }
      mediaRecorder.stop()
    })
  }, [])

  return { start, stop, updateTiles, addAudioStream, setScreenSource }
}
